import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  X, Filter, Activity, RotateCcw, Check, Plus, Search, Loader2, Save, Info,
  AlertTriangle,
} from 'lucide-react'; // eslint-disable-line no-unused-vars
import {
  getBacklogMapping,
  getBacklogDistintos,
  putBacklogFiltros,
  deleteBacklogFiltros,
  putBacklogStatus,
  deleteBacklogStatus,
} from '../api';

const monoFont = `'JetBrains Mono', 'SF Mono', ui-monospace, Menlo, monospace`;
const ACCENT = '#34d399';

const normalizeForCompare = (s) => String(s || '').trim().toUpperCase().replace(/\s+/g, ' ');
const setEquals = (a, b) => {
  if (a.length !== b.length) return false;
  const sa = new Set(a.map(normalizeForCompare));
  for (const v of b) if (!sa.has(normalizeForCompare(v))) return false;
  return true;
};

function SmallBtn({ children, onClick, disabled, color = '#64748b', bg, border, style }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '7px 12px', borderRadius: 8,
        background: bg || 'rgba(15,23,42,0.04)',
        border: `1px solid ${border || 'rgba(15,23,42,0.10)'}`,
        color: disabled ? '#94a3b8' : color,
        fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: monoFont, opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >{children}</button>
  );
}

function SearchInput({ value, onChange, placeholder }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '6px 10px', borderRadius: 8,
      background: 'rgba(15,23,42,0.04)',
      border: '1px solid rgba(15,23,42,0.08)',
      flex: 1,
    }}>
      <Search size={12} color="#64748b" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || 'Buscar…'}
        style={{
          flex: 1, background: 'transparent', border: 'none', outline: 'none',
          color: '#1e293b', fontSize: 12, fontFamily: monoFont,
        }}
      />
    </div>
  );
}

function CheckboxGrid({ allValues, draft, setDraft, accentColor = ACCENT, helperText }) {
  const [search, setSearch] = useState('');
  const draftSet = useMemo(() => new Set((draft || []).map(normalizeForCompare)), [draft]);

  const valoresFiltrados = useMemo(() => {
    const q = normalizeForCompare(search);
    const list = allValues || [];
    if (!q) return list;
    return list.filter(v => normalizeForCompare(v).includes(q));
  }, [allValues, search]);

  const toggle = (val) => {
    const key = normalizeForCompare(val);
    if (draftSet.has(key)) {
      setDraft((draft || []).filter(v => normalizeForCompare(v) !== key));
    } else {
      setDraft([...(draft || []), val]);
    }
  };

  const count = (draft || []).length;
  const total = (allValues || []).length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar valor…" />
        <span style={{ fontSize: 11, color: '#475569', fontFamily: monoFont, whiteSpace: 'nowrap' }}>
          {count} de {total} selecionados
        </span>
      </div>
      {helperText && (
        <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 8px 0' }}>{helperText}</p>
      )}
      <div style={{
        display: 'grid', gap: 6,
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        maxHeight: 260, overflowY: 'auto',
        padding: 8, borderRadius: 8,
        background: 'rgba(15,23,42,0.04)',
        border: '1px solid rgba(15,23,42,0.06)',
      }}>
        {valoresFiltrados.length === 0 && (
          <div style={{ color: '#64748b', fontSize: 11, padding: 8 }}>Nada encontrado.</div>
        )}
        {valoresFiltrados.map((v) => {
          const checked = draftSet.has(normalizeForCompare(v));
          return (
            <label
              key={v}
              onClick={() => toggle(v)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 9px', borderRadius: 6,
                background: checked ? `${accentColor}14` : 'rgba(15,23,42,0.02)',
                border: `1px solid ${checked ? accentColor + '55' : 'rgba(15,23,42,0.08)'}`,
                cursor: 'pointer', userSelect: 'none',
                fontFamily: monoFont, fontSize: 11.5,
                color: checked ? '#0f172a' : '#334155',
              }}
            >
              <span style={{
                width: 14, height: 14, borderRadius: 3,
                background: checked ? accentColor : 'rgba(15,23,42,0.08)',
                border: `1px solid ${checked ? accentColor : 'rgba(15,23,42,0.12)'}`,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {checked && <Check size={10} color="#ffffff" strokeWidth={3} />}
              </span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function FiltrosAbaEditor({ abaKey, abaData, allTiposServico, onSaved }) {
  const initial = useMemo(() => abaData?.effective_filtros_tipo_servico || [], [abaData]);
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(abaData?.effective_filtros_tipo_servico || []);
  }, [abaData]);

  const defaultFiltros = abaData?.default_filtros_tipo_servico || [];
  const isDirty = !setEquals(draft, abaData?.effective_filtros_tipo_servico || []);
  const matchesDefault = setEquals(draft, defaultFiltros);
  const canSave = isDirty && (draft.length > 0);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await putBacklogFiltros(abaKey, draft);
      onSaved && onSaved();
    } catch (e) {
      alert('Erro ao salvar: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!abaData?.is_overridden) return;
    if (!window.confirm('Resetar pro default desta aba?')) return;
    setSaving(true);
    try {
      await deleteBacklogFiltros(abaKey);
      onSaved && onSaved();
    } catch (e) {
      alert('Erro ao resetar: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  return (
    <div style={{
      padding: 14, borderRadius: 10,
      background: 'rgba(15,23,42,0.04)',
      border: '1px solid rgba(15,23,42,0.08)',
      marginBottom: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{abaData?.titulo || abaKey}</span>
        {abaData?.is_overridden && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 7px', borderRadius: 999,
            fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em',
            background: 'rgba(251,191,36,0.12)',
            border: '1px solid rgba(251,191,36,0.45)',
            color: '#b45309', fontFamily: monoFont, textTransform: 'uppercase',
          }}>
            <AlertTriangle size={9} />Editado
          </span>
        )}
        {matchesDefault && !abaData?.is_overridden && (
          <span style={{ fontSize: 10, color: '#64748b', fontFamily: monoFont }}>(padrão)</span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 10.5, color: '#64748b', fontFamily: monoFont }}>
          tipo_servico → entra nesta aba se ∈ lista
        </span>
      </div>

      <CheckboxGrid
        allValues={allTiposServico}
        draft={draft}
        setDraft={setDraft}
        accentColor={ACCENT}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
        {abaData?.is_overridden && (
          <SmallBtn onClick={handleReset} color="#be123c" border="rgba(248,113,113,0.30)" bg="rgba(248,113,113,0.08)">
            <RotateCcw size={11} /> Resetar
          </SmallBtn>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 10.5, color: '#64748b', fontFamily: monoFont }}>
          {!isDirty ? 'Sem alterações' : (draft.length === 0 ? 'Marque ao menos 1 tipo' : 'Alterações pendentes')}
        </span>
        <SmallBtn
          onClick={handleSave}
          disabled={!canSave || saving}
          color={canSave ? '#ffffff' : '#94a3b8'}
          bg={canSave ? 'linear-gradient(180deg, #34d399, #10b981)' : 'rgba(15,23,42,0.04)'}
          border={canSave ? 'rgba(52,211,153,0.6)' : 'rgba(15,23,42,0.10)'}
        >
          {saving ? <Loader2 size={11} /> : <Save size={11} />}
          Salvar
        </SmallBtn>
      </div>
    </div>
  );
}

function StatusExcluidosEditor({ statusRule, allStatus, onSaved }) {
  const initial = statusRule?.effective_excluidos || [];
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(statusRule?.effective_excluidos || []); }, [statusRule]);

  const isDirty = !setEquals(draft, statusRule?.effective_excluidos || []);
  const canSave = isDirty;

  // Mesclar allStatus com os já selecionados (em caso de planilha vazia ou status ainda não importado)
  const statusOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const v of (statusRule?.effective_excluidos || [])) {
      const k = normalizeForCompare(v);
      if (!seen.has(k)) { seen.add(k); out.push(v); }
    }
    for (const v of (statusRule?.default_excluidos || [])) {
      const k = normalizeForCompare(v);
      if (!seen.has(k)) { seen.add(k); out.push(v); }
    }
    for (const v of (allStatus || [])) {
      const k = normalizeForCompare(v);
      if (!seen.has(k)) { seen.add(k); out.push(v); }
    }
    return out.sort((a, b) => a.localeCompare(b));
  }, [allStatus, statusRule]);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await putBacklogStatus(draft);
      onSaved && onSaved();
    } catch (e) {
      alert('Erro ao salvar: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!statusRule?.is_overridden) return;
    if (!window.confirm('Resetar status excluídos pro default?')) return;
    setSaving(true);
    try {
      await deleteBacklogStatus();
      onSaved && onSaved();
    } catch (e) {
      alert('Erro ao resetar: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  return (
    <div style={{
      padding: 14, borderRadius: 10,
      background: 'rgba(15,23,42,0.04)',
      border: '1px solid rgba(15,23,42,0.08)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
        padding: '10px 12px', borderRadius: 8,
        background: 'rgba(96,165,250,0.06)',
        border: '1px solid rgba(96,165,250,0.20)',
      }}>
        <Info size={14} color="#60a5fa" />
        <span style={{ fontSize: 12, color: '#334155', flex: 1 }}>
          OS com qualquer um dos status marcados abaixo é <strong>excluída</strong> do backlog.
          Default: <code style={{ fontFamily: monoFont, color: '#b45309' }}>{(statusRule?.default_excluidos || []).join(', ') || '(vazio)'}</code>
        </span>
        {statusRule?.is_overridden && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 7px', borderRadius: 999,
            fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em',
            background: 'rgba(251,191,36,0.12)',
            border: '1px solid rgba(251,191,36,0.45)',
            color: '#b45309', fontFamily: monoFont, textTransform: 'uppercase',
          }}>
            <AlertTriangle size={9} />Editado
          </span>
        )}
      </div>

      <CheckboxGrid
        allValues={statusOptions}
        draft={draft}
        setDraft={setDraft}
        accentColor="#fbbf24"
        helperText='Marque os status que devem ser ignorados na contagem do backlog.'
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
        {statusRule?.is_overridden && (
          <SmallBtn onClick={handleReset} color="#be123c" border="rgba(248,113,113,0.30)" bg="rgba(248,113,113,0.08)">
            <RotateCcw size={11} /> Resetar
          </SmallBtn>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 10.5, color: '#64748b', fontFamily: monoFont }}>
          {!isDirty ? 'Sem alterações' : 'Alterações pendentes'}
        </span>
        <SmallBtn
          onClick={handleSave}
          disabled={!canSave || saving}
          color={canSave ? '#ffffff' : '#94a3b8'}
          bg={canSave ? 'linear-gradient(180deg, #fbbf24, #d97706)' : 'rgba(15,23,42,0.04)'}
          border={canSave ? 'rgba(251,191,36,0.6)' : 'rgba(15,23,42,0.10)'}
        >
          {saving ? <Loader2 size={11} /> : <Save size={11} />}
          Salvar
        </SmallBtn>
      </div>
    </div>
  );
}

export default function BacklogMapeamentoModal({ open, onClose, ascCode = '2326126' }) {
  const [mapping, setMapping] = useState(null);
  const [distintos, setDistintos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('filtros');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [m, d] = await Promise.all([
        getBacklogMapping(),
        getBacklogDistintos(ascCode),
      ]);
      setMapping(m.data);
      setDistintos(d.data);
    } catch (e) {
      console.error('Backlog mapping load error', e);
    }
    setLoading(false);
  }, [ascCode]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 1100, maxHeight: '90vh',
          background: '#ffffff',
          border: '1px solid rgba(15,23,42,0.08)',
          borderRadius: 14,
          display: 'flex', flexDirection: 'column',
          color: '#1e293b', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '16px 20px',
          borderBottom: '1px solid rgba(15,23,42,0.08)',
          background: `linear-gradient(180deg, ${ACCENT}12 0%, transparent 100%)`,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: `${ACCENT}22`,
            border: `1px solid ${ACCENT}55`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Activity size={18} color={ACCENT} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#0f172a' }}>
              Mapeamento do Backlog
            </h2>
            <p style={{ margin: '2px 0 0 0', fontSize: 11.5, color: '#64748b' }}>
              Filtros de tipo de serviço por aba + status que NÃO contam como backlog
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(15,23,42,0.04)',
            border: '1px solid rgba(15,23,42,0.10)',
            borderRadius: 8, padding: 6, cursor: 'pointer',
            color: '#334155',
          }}>
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 4,
          padding: '0 20px',
          borderBottom: '1px solid rgba(15,23,42,0.08)',
        }}>
          {[
            { key: 'filtros', label: 'Filtros por Aba', Icone: Filter },
            { key: 'status', label: 'Status Excluídos', Icone: Activity },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setActiveSection(t.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '12px 14px', background: 'transparent',
                border: 'none', cursor: 'pointer',
                borderBottom: `2px solid ${activeSection === t.key ? ACCENT : 'transparent'}`,
                color: activeSection === t.key ? '#0f172a' : '#475569',
                fontSize: 12.5, fontWeight: 700,
                fontFamily: monoFont,
              }}
            >
              <t.Icone size={13} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
              <Loader2 size={24} color="#475569" />
              <span style={{ marginLeft: 8, color: '#475569', fontFamily: monoFont }}>Carregando…</span>
            </div>
          )}

          {!loading && mapping && activeSection === 'filtros' && (
            <>
              {Object.entries(mapping.abas).map(([abaKey, abaData]) => (
                <FiltrosAbaEditor
                  key={abaKey}
                  abaKey={abaKey}
                  abaData={abaData}
                  allTiposServico={distintos?.tipo_servico || []}
                  onSaved={refresh}
                />
              ))}
              {(distintos?.tipo_servico || []).length === 0 && (
                <p style={{ color: '#64748b', fontSize: 11, fontStyle: 'italic', textAlign: 'center', padding: 14 }}>
                  Nenhuma OS importada no backlog ainda — suba a planilha pelo card "Backlog" pra alimentar a lista de tipos de serviço.
                </p>
              )}
            </>
          )}

          {!loading && mapping && activeSection === 'status' && (
            <StatusExcluidosEditor
              statusRule={mapping.status_rule}
              allStatus={distintos?.status || []}
              onSaved={refresh}
            />
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          padding: '12px 20px',
          borderTop: '1px solid rgba(15,23,42,0.08)',
        }}>
          <span style={{ fontSize: 10.5, color: '#64748b', fontFamily: monoFont, marginRight: 12 }}>
            Cada aba salva independentemente
          </span>
          <SmallBtn onClick={onClose}>Fechar</SmallBtn>
        </div>
      </div>
    </div>
  );
}
